/**
 * Hook for restoring scroll position when navigating between routes/tabs
 * Saves scroll position on unmount and restores it on mount
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

interface UseRouteScrollRestorationOptions {
  /** Custom storage key (defaults to pathname-based key) */
  key?: string;
  /** Enable/disable scroll restoration */
  enabled?: boolean;
  /** Delay in ms before restoring scroll (allows content to render) */
  delayMs?: number;
  /** Chỉ restore khi back/forward (POP). PUSH/REPLACE → cuộn lên đầu. Default false = luôn restore (giữ hành vi cũ). */
  restoreOnlyOnBack?: boolean;
}

export function useRouteScrollRestoration({
  key,
  enabled = true,
  delayMs = 50,
  restoreOnlyOnBack = false
}: UseRouteScrollRestorationOptions = {}) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const storageKey = key || `scroll:${location.pathname}`;
  const hasRestored = useRef(false);

  // Save scroll position
  const saveScrollPosition = useCallback(() => {
    if (!enabled) return;
    
    const scrollY = window.scrollY;
    if (scrollY > 0) {
      sessionStorage.setItem(storageKey, String(scrollY));
    }
  }, [storageKey, enabled]);

  // Restore scroll position
  const restoreScrollPosition = useCallback(() => {
    if (!enabled || hasRestored.current) return;

    // Smart mode: điều hướng mới (PUSH/REPLACE) → lên đầu thay vì restore.
    if (restoreOnlyOnBack && navigationType !== 'POP') {
      window.scrollTo({ top: 0, behavior: 'auto' });
      hasRestored.current = true;
      return;
    }
    
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      const position = parseInt(saved, 10);
      if (!isNaN(position) && position > 0) {
        setTimeout(() => {
          window.scrollTo({ top: position, behavior: 'auto' });
          hasRestored.current = true;
        }, delayMs);
      }
    } else {
      hasRestored.current = true;
    }
  }, [storageKey, delayMs, enabled, restoreOnlyOnBack, navigationType]);

  // Save on unmount
  useEffect(() => {
    if (!enabled) return;
    return () => saveScrollPosition();
  }, [enabled, saveScrollPosition]);

  // Restore on mount
  useEffect(() => {
    if (!enabled) return;
    hasRestored.current = false;
    restoreScrollPosition();
  }, [enabled, restoreScrollPosition]);

  return { saveScrollPosition, restoreScrollPosition };
}
